import classNames from "classnames";
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {createPortal} from "react-dom";
import {motion, AnimatePresence} from "framer-motion";
import {AiOutlineLoading3Quarters} from "react-icons/ai";

const toastVariants = {
  hidden: {opacity: 0, y: 30, scale: 0},
  visible: {opacity: 1, y: 0, scale: 1},
  exit: {opacity: 0, y: 30, scale: 0},
};

type ToastProps = {
  message: string;
  type: "success" | "error";
  id: number;
  onClose: (id: number) => void;
  toastTimer?: number;
};

type ToastType = {id: number; message: string; type: "success" | "error"};

type ToastContextType = {
  showToast: (message: string, type: "success" | "error") => void;
};

const ToastContext = React.createContext<ToastContextType>(
  {} as ToastContextType,
);

export const useToast = () => useContext(ToastContext);

const Toast = ({message, type, id, onClose, toastTimer}: ToastProps) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(id), toastTimer || 3000);
    return () => clearTimeout(timer);
  }, [id, onClose]);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={toastVariants}
      transition={{duration: 0.4, type: "spring", damping: 15, stiffness: 200}}
      className={classNames(
        "flex items-center justify-between px-6 py-3 rounded-md text-sm text-white font-semibold",
        {
          "bg-green-500": type === "success",
          "bg-red-500 ": type === "error",
        },
      )}
    >
      <span>{message}</span>
      <button onClick={() => onClose(id)} className="ml-4">
        ✕
      </button>
    </motion.div>
  );
};

export const ToastProvider = ({
  children,
  loading,
  loadingMessage,
  toastTimer,
}: {
  children: ReactNode;
  loading?: boolean;
  loadingMessage?: string;
  toastTimer?: number;
}) => {
  const [toasts, setToasts] = useState<ToastType[]>([]);

  const showToast = useCallback(
    (message: string, type: "success" | "error") => {
      const id = Math.random();
      setToasts((prev) => [...prev, {message, type, id}]);
    },
    [],
  );

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{showToast}}>
      {children}
      {createPortal(
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 space-y-2">
          <AnimatePresence>
            {toasts.map((toast) => (
              <Toast
                key={toast.id}
                {...toast}
                onClose={removeToast}
                toastTimer={toastTimer}
              />
            ))}
            {!!loading && (
              <div className="w-full flex justify-center">
                <motion.div
                  key="loader"
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={toastVariants}
                  transition={{
                    duration: 0.4,
                    type: "spring",
                    damping: 15,
                    stiffness: 200,
                  }}
                  className="flex items-center gap-3 px-8 py-3 rounded-md text-sm text-base-100 bg-blue-400 w-fit"
                >
                  {loadingMessage || "Loading...."}
                  <AiOutlineLoading3Quarters className="animate-spin" />
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
};
